import type { SimulationParams, SimulationResponse } from '../types';
import type { ROIBounds } from '@/contexts/MapInteractionContext';

// Get simulation API URL from environment or use default
const SIMULATION_API_URL = (import.meta as any).env?.VITE_SIMULATION_API_URL || 'http://localhost:8080';

export interface SimulationPayload {
  dem_url: string;
  buildings_url: string;
  hyetograph_url?: string;
  params: {
    manning_n: number;
    infiltration_factor: number;
    rainfall_rate: number;
    rainfall_units: 'mm/hr' | 'mm/min';
    duration_minutes: number;
    dt_seconds: number;
    min_pond_size_pixels: number;
    min_pond_depth_meters: number;
    bounds?: {
      west: number;
      east: number;
      north: number;
      south: number;
    };
  };
}

/**
 * Run CFD simulation via the simulation service API
 * @param demUrl GCS URL of the DEM file
 * @param buildingsUrl GCS URL of the buildings mask file
 * @param params Simulation parameters
 * @param hyetographUrl Optional GCS URL of hyetograph CSV
 * @param roiBounds Optional region of interest bounds
 * @returns Simulation response with results and output file URLs
 */
export async function runSimulation(
  demUrl: string,
  buildingsUrl: string,
  params: SimulationParams,
  hyetographUrl?: string,
  roiBounds?: ROIBounds | null
): Promise<SimulationResponse> {
  // Build payload
  const payload: SimulationPayload = {
    dem_url: demUrl,
    buildings_url: buildingsUrl,
    params: {
      manning_n: params.manning_n,
      infiltration_factor: params.infiltration_factor,
      rainfall_rate: params.rainfall_rate,
      rainfall_units: params.rainfall_units,
      duration_minutes: params.duration_minutes,
      dt_seconds: params.dt_seconds,
      min_pond_size_pixels: params.min_pond_size_pixels,
      min_pond_depth_meters: params.min_pond_depth_meters,
    }
  };

  // Add hyetograph if provided
  if (hyetographUrl) {
    payload.hyetograph_url = hyetographUrl;
  }

  // Add bounds (ROI) if selected
  if (roiBounds) {
    payload.params.bounds = {
      west: roiBounds.west,
      east: roiBounds.east,
      north: roiBounds.north,
      south: roiBounds.south,
    };
  }

  const response = await fetch(`${SIMULATION_API_URL}/api/simulate-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Simulation failed');
  }

  return await response.json();
}
